<div class="flex min-w-0 flex-col space-y-5 p-5">

  <!-- Header -->
  <div class="flex items-start justify-between gap-4">
    <div>
      <h2 class="text-sm font-semibold text-slate-900">Bug reports</h2>
      <p class="mt-0.5 text-xs text-slate-500">
        Submitted from the dashboard's "Report a bug" dialog. Each report includes the page, browser and window size.
      </p>
    </div>
    <button
      (click)="load()"
      class="flex flex-shrink-0 items-center gap-1 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
      title="Refresh"
    >
      <lucide-icon name="refresh-cw" [size]="11" [strokeWidth]="2"></lucide-icon>
      Refresh
    </button>
  </div>

  <!-- Status tabs -->
  <div class="flex flex-wrap items-center gap-1.5">
    <button
      (click)="setFilter('ALL')"
      class="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
      [ngClass]="filter === 'ALL' ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'"
    >
      All <span class="ml-1 text-slate-400">{{ countFor('ALL') }}</span>
    </button>
    <button
      *ngFor="let option of statusOptions"
      (click)="setFilter(option.value)"
      class="rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors"
      [ngClass]="filter === option.value ? 'border-violet-300 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'"
    >
      {{ option.label }} <span class="ml-1 text-slate-400">{{ countFor(option.value) }}</span>
    </button>
  </div>

  <!-- Action error -->
  <div *ngIf="actionError"
       class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
    <lucide-icon name="circle-x" [size]="16" [strokeWidth]="1.75"></lucide-icon>
    {{ actionError }}
  </div>

  <!-- Skeleton -->
  <div *ngIf="loading" class="space-y-2">
    <div *ngFor="let _ of [1,2,3,4,5,6]" class="h-10 animate-pulse rounded-lg bg-slate-100"></div>
  </div>

  <!-- Load error -->
  <div *ngIf="!loading && error"
       class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
    <lucide-icon name="circle-x" [size]="16" [strokeWidth]="1.75"></lucide-icon>
    {{ error }}
  </div>

  <!-- Empty -->
  <div *ngIf="!loading && !error && reports.length === 0"
       class="flex flex-col items-center justify-center py-16 text-center">
    <div class="flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-100">
      <lucide-icon name="bug" [size]="22" class="text-zinc-400" [strokeWidth]="1.5"></lucide-icon>
    </div>
    <p class="mt-3 text-sm font-medium text-zinc-700">No bug reports</p>
    <p class="mt-1 text-xs text-zinc-400">
      {{ filter === 'ALL' ? 'Reports submitted from the dashboard will appear here.' : 'Nothing in this status.' }}
    </p>
  </div>

  <!-- Table -->
  <div *ngIf="!loading && !error && reports.length > 0"
       class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <table class="min-w-full divide-y divide-slate-100 text-xs">
      <thead class="bg-slate-50">
        <tr>
          <th class="w-6 px-3 py-2.5"></th>
          <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Submitted</th>
          <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Reporter</th>
          <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Page</th>
          <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Report</th>
          <th class="px-3 py-2.5 text-left font-semibold text-slate-500">Status</th>
        </tr>
      </thead>
      <tbody class="divide-y divide-slate-100">
        <ng-container *ngFor="let report of reports">
          <tr class="cursor-pointer align-top hover:bg-slate-50" (click)="toggle(report)">
            <td class="px-3 py-2.5 text-slate-400">
              <lucide-icon [name]="expandedId === report.id ? 'chevron-down' : 'chevron-right'" [size]="13" [strokeWidth]="2"></lucide-icon>
            </td>
            <td class="whitespace-nowrap px-3 py-2.5 text-slate-500">{{ report.createdAt | date:'MMM d, h:mm a' }}</td>
            <td class="px-3 py-2.5">
              <p class="font-medium text-slate-800">{{ report.reporterName || report.reporterEmail || 'Unknown' }}</p>
              <p *ngIf="report.reporterName" class="text-slate-400">{{ report.reporterEmail }}</p>
            </td>
            <td class="max-w-[180px] truncate px-3 py-2.5 font-mono text-slate-500" [title]="report.pageUrl">{{ report.pageUrl || '—' }}</td>
            <td class="max-w-md px-3 py-2.5 text-slate-700">
              <p [class.line-clamp-2]="expandedId !== report.id" class="whitespace-pre-wrap">{{ report.description }}</p>
            </td>
            <td class="px-3 py-2.5" (click)="$event.stopPropagation()">
              <select
                [ngModel]="report.status"
                (ngModelChange)="updateStatus(report, $event)"
                [disabled]="updatingId === report.id"
                class="rounded-full border px-2 py-0.5 text-[11px] font-semibold focus:outline-none focus:ring-2 focus:ring-violet-400 disabled:opacity-60"
                [ngClass]="badgeFor(report.status)"
                [attr.aria-label]="'Status for report from ' + (report.reporterEmail || 'unknown')"
              >
                <option *ngFor="let option of statusOptions" [ngValue]="option.value">{{ option.label }}</option>
              </select>
            </td>
          </tr>

          <!-- Expanded context -->
          <tr *ngIf="expandedId === report.id" class="bg-slate-50/60">
            <td></td>
            <td colspan="5" class="px-3 pb-4 pt-1">
              <dl class="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs">
                <dt class="text-slate-400">Page</dt>
                <dd class="break-all font-mono text-slate-700">{{ report.pageUrl || '—' }}</dd>
                <dt class="text-slate-400">Browser</dt>
                <dd class="break-all text-slate-700">{{ report.userAgent || '—' }}</dd>
                <dt class="text-slate-400">Window</dt>
                <dd class="text-slate-700">{{ report.viewport || '—' }}</dd>
                <dt class="text-slate-400">User ID</dt>
                <dd class="font-mono text-slate-700">{{ report.reporterId }}</dd>
                <dt class="text-slate-400">Organization</dt>
                <dd class="text-slate-700">
                  <a *ngIf="report.orgId" [routerLink]="['/subscriptions']" [queryParams]="{ org: report.orgId }"
                     class="font-mono text-violet-600 hover:underline">{{ report.orgId }}</a>
                  <span *ngIf="!report.orgId">—</span>
                </dd>
                <dt class="text-slate-400">Report ID</dt>
                <dd class="font-mono text-slate-700">{{ report.id }}</dd>
                <ng-container *ngIf="report.updatedBy">
                  <dt class="text-slate-400">Last updated</dt>
                  <dd class="text-slate-700">{{ labelFor(report.status) }} by {{ report.updatedBy }}, {{ report.updatedAt | date:'MMM d, h:mm a' }}</dd>
                </ng-container>
              </dl>
            </td>
          </tr>
        </ng-container>
      </tbody>
    </table>

    <div *ngIf="nextCursor" class="border-t border-slate-100 px-3 py-2.5 text-center">
      <button
        (click)="loadMore()"
        [disabled]="loadingMore"
        class="text-xs font-medium text-violet-600 hover:underline disabled:opacity-60"
      >
        {{ loadingMore ? 'Loading...' : 'Load more' }}
      </button>
    </div>
  </div>
</div>
